import { BrowserRouter as Router,
    Switch,
    Route,
    Link,
    Redirect,
    useParams, useLocation} from 'react-router-dom';
import { useRecoilValue, useRecoilState } from 'recoil';

import { Nav, PrivateRoute, ClassPicker } from '_components';
import { history } from '_helpers';
import { Home } from 'home';
import { Account } from '_components/account';
import { Dashboard } from '_components/dashboard';
import { Profile } from '_components/profile';
import { StudentInfoList } from '_components/studentInfoList';
import { StudentScoreList, PersonalScore } from '_components/studentScoreList';
import { useAuthWrapper, useClassWrapper } from '_helpers';
import { ROLE_GROUPS, ROLES } from '_helpers/roles';
import { classSlug } from '_helpers/normalize';
import { authAtom, classPickerVisibleAtom } from '_state';
import { Layout, Button, Row, Col, Menu, Dropdown } from 'antd';
import {
    MenuOutlined, DashboardOutlined, InfoCircleOutlined,
    TableOutlined, CalendarOutlined, UserOutlined, TeamOutlined,
    AppstoreOutlined, FileTextOutlined, BellOutlined, LogoutOutlined, BulbOutlined,
    DesktopOutlined, CheckOutlined
} from '@ant-design/icons';

import React, { useEffect, useState } from 'react';
import { loadingVisibleAtom } from '_state';
import Title from 'antd/lib/typography/Title';
import { useUserActions } from '_actions';
import {Notification} from './_components/bach_component/Notification/Notification'
import { socketWrapper } from '_helpers/socket-wrapper';
import Socket from '_components/bach_component/Socket/socket';
import LinearProgress from '@mui/material/LinearProgress';
import { DBPortal } from '_components/dbportal/DBPortal';
import { ManagerDashboard } from '_components/manager/ManagerDashboard';
import { AllStudentsPage } from '_components/manager/AllStudentsPage';
import { TeacherKpiPage } from '_components/manager/TeacherKpiPage';
import { StaffManagePage } from '_components/manager/StaffManagePage';
import { StuHome } from 'home/StuHome';
import { LandingPage } from '_components/landing/LandingPage';
import { AttendancePage } from '_components/attendance';
import { WeekCalendar } from '_components/schedule';
import { HomeAssignmentPage } from '_components/home-assignment';
import { PublicUpload } from '_components/public-upload/PublicUpload';
import { TuitionPage } from '_components/tuition';
import { NotificationsPage } from '_components/notifications';
import { OneOnOnePage } from '_components/oneonone';
import { PanelPage } from '_components/panel';
const style = { };

const { Header, Footer, Content } = Layout;

export { App };

function App() {
    const authWrapper = useAuthWrapper();
    const classWrapper = useClassWrapper();
    const [drawerVisible, setDrawerVisible] = useRecoilState(classPickerVisibleAtom);
    const [loadingVisible, setLoadingVisible] = useRecoilState(loadingVisibleAtom);
    const [navCollapsed, setNavCollapsed] = useState(typeof window !== 'undefined' && window.innerWidth < 992);
    // Narrow = header has no room for the class panel → class tabs fall back into the sidebar.
    const [isNarrow, setIsNarrow] = useState(typeof window !== 'undefined' && window.innerWidth < 992);
    // Theme preference: 'light' | 'dark' | 'system'. 'system' follows the OS via
    // prefers-color-scheme. Resolved to a concrete <html data-theme> here and pre-paint in index.html.
    const [themePref, setThemePref] = useState(() => {
        try {
            return localStorage.getItem('themePref')
                || (localStorage.getItem('theme') === 'dark' ? 'dark'
                    : localStorage.getItem('theme') === 'light' ? 'light' : 'system');
        } catch (e) { return 'system'; }
    });
    useEffect(() => {
        const apply = (pref) => {
            const dark = pref === 'dark'
                || (pref === 'system' && window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches);
            document.documentElement.setAttribute('data-theme', dark ? 'dark' : 'light');
        };
        apply(themePref);
        try { localStorage.setItem('themePref', themePref); } catch (e) {}
        if (themePref !== 'system' || !window.matchMedia) return;
        const mq = window.matchMedia('(prefers-color-scheme: dark)');
        const onChange = () => apply('system');
        mq.addEventListener ? mq.addEventListener('change', onChange) : mq.addListener(onChange);
        return () => { mq.removeEventListener ? mq.removeEventListener('change', onChange) : mq.removeListener(onChange); };
    }, [themePref]);
    const THEME_OPTS = [
        { key: 'light',  label: 'Sáng',    icon: <BulbOutlined /> },
        { key: 'dark',   label: 'Tối',     icon: <BulbOutlined /> },
        { key: 'system', label: 'Hệ thống', icon: <DesktopOutlined /> },
    ];
    useEffect(() => {
        const onResize = () => setIsNarrow(window.innerWidth < 992);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    const userData = JSON.parse(localStorage.getItem("userData"));
    useEffect(() => {
        if (!authWrapper.tokenValue && !userData) {
            authWrapper.loadUser().catch(() => {});
        }
        // Recover a valid httpOnly-cookie session when localStorage was cleared.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authWrapper.tokenValue, userData?.student_id]);

    useEffect(() => {
        if (authWrapper.tokenValue && userData) {
            classWrapper.getClassList();
        }
        // Load once per authenticated session so the sidebar can restore the saved/first class.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authWrapper.tokenValue, userData?.role]);
    
    const userActions = useUserActions();
    const showDrawer = () => {
        classWrapper.getClassList();
        setDrawerVisible(true);
    };
    useEffect(() => {
        const open = () => showDrawer();
        window.addEventListener('open-class-picker', open);
        return () => window.removeEventListener('open-class-picker', open);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onDrawerClose = () => {
        setDrawerVisible(false);
    };
  

    return (
        <div className={'app-container' + (authWrapper.tokenValue ? ' bg-light' : '')}>
            {/* <div>{JSON.stringify(authWrapper.tokenValue)}</div> */}
            <Router history={history}>
            {authWrapper.tokenValue && <Socket></Socket>}
            <Switch>
            {/* Landing page — shown to unauthenticated visitors at root */}
            {!authWrapper.tokenValue && <Route exact path="/" component={LandingPage} />}
            {/* Public upload page — no auth, no Layout. Must come before the Layout block. */}
            <Route exact path="/upload/:token" component={PublicUpload} />
            <Route render={() => (
            <>
            <Layout>
                <Header className="app-header">
                    <div className="app-header-inner">
                        {authWrapper.tokenValue &&
                            <Button
                                className="hamburger-btn"
                                type="text"
                                aria-label="Menu"
                                icon={<MenuOutlined />}
                                onClick={() => setNavCollapsed(v => !v)}
                                style={{ fontSize: '18px', marginRight: 4 }}
                            />}
                        <Link to="/" className="app-header-logo">
                            <img src="/logos/MASON_logo-ngang-bl.png" alt="The Mason" className="logo-light" />
                            <img src="/logos/MASON_logo-ngang-w.png" alt="The Mason" className="logo-dark" />
                        </Link>
                        {authWrapper.tokenValue !== "" &&
                            <span className="app-breadcrumb"><ClassNameDisplay /></span>}
                        <span style={{ flex: '1 1 auto' }} />
                        {authWrapper.tokenValue !== "" &&
                            <Dropdown
                                trigger={['hover', 'click']}
                                placement="bottomRight"
                                overlay={
                                    <Menu className="avatar-menu">
                                        <div className="avatar-menu-head">
                                            <div className="avatar-menu-name">{userData?.name || 'Tài khoản'}</div>
                                            <div className="avatar-menu-sub">{userData?.student_id || ''}</div>
                                        </div>
                                        <Menu.Item key="noti" icon={<BellOutlined />}><Link to="/notifications">Thông báo</Link></Menu.Item>
                                        <Menu.Item key="profile" icon={<UserOutlined />}><Link to="/profile">Hồ sơ cá nhân</Link></Menu.Item>
                                        <Menu.ItemGroup title="Giao diện">
                                            {THEME_OPTS.map(o => (
                                                <Menu.Item
                                                    key={`theme-${o.key}`}
                                                    icon={o.icon}
                                                    onClick={() => setThemePref(o.key)}
                                                >
                                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                                        {o.label}
                                                        {themePref === o.key && <CheckOutlined style={{ fontSize: 12, color: 'var(--accent)' }} />}
                                                    </span>
                                                </Menu.Item>
                                            ))}
                                        </Menu.ItemGroup>
                                        <Menu.Divider />
                                        <Menu.Item key="logout" danger icon={<LogoutOutlined />} onClick={userActions.logout}>Đăng xuất</Menu.Item>
                                    </Menu>
                                }
                            >
                                <span className="header-avatar" role="button" tabIndex={0} aria-label="Tài khoản">
                                    {(userData?.name || '?').trim().split(/\s+/).slice(-2).map(w => w[0]).join('').toUpperCase()}
                                </span>
                            </Dropdown>}
                    </div>
                </Header>
                <Layout>
                    <Nav onLogout={userActions.logout} auth={authWrapper.tokenValue} userData={userData} classID={classWrapper.curClass ? classWrapper.curClass.class_id : ""} collapsed={navCollapsed} onCollapse={setNavCollapsed} onChooseClass={showDrawer} curClass={classWrapper.curClass}/>
                     
                <Layout>

                    <Content className="app-content">
                        <Switch>
                          <PrivateRoute exact path="/" component={Home} />
                          {/* <PrivateRoute exact path="/:classID" children={<Child classID="123"/>} component={Child}/>  */}
                          <Route path="/account" component={Account} />
                          <PrivateRoute path="/profile" component={Profile} />
                          <PrivateRoute exact path="/dbportal" component={DBPortal} />
                          <PrivateRoute exact path="/manager" component={ManagerDashboard} />
                          {ROLE_GROUPS.STAFF.includes(userData?.role) &&
                            <PrivateRoute exact path="/schedule" component={WeekCalendar} />}
                          {ROLE_GROUPS.MANAGERS.includes(userData?.role) &&
                            <PrivateRoute exact path="/manager/teachers" component={TeacherKpiPage} />}
                          {ROLE_GROUPS.MANAGERS.includes(userData?.role) &&
                            <PrivateRoute exact path="/manager/staff" component={StaffManagePage} />}
                          <PrivateRoute exact path="/manager/students" component={AllStudentsPage} />
                          <PrivateRoute exact path="/tuition" component={TuitionPage} />
                          <PrivateRoute exact path="/notifications" component={NotificationsPage} />
                          <PrivateRoute exact path="/one-on-one" component={OneOnOnePage} />
                          <PrivateRoute exact path="/stuhome" component={StuHome} />
                          <PrivateRoute exact path="/personalscore" component={PersonalScore}/>
                          {userData?.role === ROLES.ADMIN &&
                            <PrivateRoute exact path="/panel" component={PanelPage} />}
                          <PrivateRoute path="/:classID" component={Child} />
                          <Redirect from="*" to="/" />
                        </Switch>
                    </Content>
    
             </Layout>
             </Layout> 
            </Layout>
            
            <ClassPicker drawerVisible = {drawerVisible} setDrawerVisible = {setDrawerVisible} onDrawerClose={onDrawerClose}/>
            <Notification></Notification>
            <LinearProgress sx={{position:"fixed", width: "100%", top: "0px", zIndex:2, visibility: (loadingVisible ? "visible" : "hidden"), backgroundColor: 'var(--accent-tint)', '& .MuiLinearProgress-bar': { backgroundColor: 'var(--accent)' }}} />
            </>
            )} />
            </Switch>
            </Router>
        </div>
    );
}

function Child(props) {
    let {classID} = useParams();
    const userData = JSON.parse(localStorage.getItem("userData"));
    const classWrapper = useClassWrapper();
    const [loaded, setloaded] = useState(false);
    const [loadState, setLoadState] = useState({ status: 'loading', class: null });
    // console.log('hello');
    const location  = useLocation()
    // let { path } = match;
    // console.log(path);
    useEffect(() => {
        let cancelled = false;
        setloaded(false);
        setLoadState({ status: 'loading', class: null });
        classWrapper.chooseClassById(classID).then(result => {
            if (!cancelled) {
                setLoadState(result && result.status ? result : { status: result ? 'success' : 'not_found', class: result });
                setloaded(true);
            }
        });
        console.log("Child component construct, classID: ", classID)
        return () => { cancelled = true; };
        // Re-select when the route changes; classWrapper is intentionally omitted to avoid a render loop.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    },[classID])

    if (!loaded || loadState.status === 'loading') {
        return (
            <div className="att2-page soft-in">
                <div className="att2-metric">Đang tải lớp học...</div>
            </div>
        );
    }

    if (loadState.status === 'error') {
        return (
            <div className="att2-page soft-in">
                <div className="att2-empty">
                    <h2>Không tải được lớp</h2>
                    <p>Kiểm tra mạng rồi thử lại.</p>
                    <Button onClick={() => window.location.reload()}>Thử lại</Button>
                    <Link to="/" style={{ marginLeft: 8 }}><Button>Về trang chủ</Button></Link>
                </div>
            </div>
        );
    }

    if (!loadState.class) {
        return (
            <div className="att2-page soft-in">
                <div className="att2-empty">
                    <h2>Không vào được lớp này</h2>
                    <p>Lớp đã bị xóa, đổi mã, hoặc tài khoản của bạn không có quyền xem.</p>
                    {ROLE_GROUPS.STAFF.includes(userData.role) &&
                        <Button type="primary" onClick={() => classWrapper.getClassList().then(() => {
                            try { localStorage.setItem('classPickerOpenHint', '1'); } catch (e) {}
                            window.dispatchEvent(new Event('open-class-picker'));
                        })}>Chọn lớp</Button>}
                    <Link to="/" style={{ marginLeft: 8 }}><Button>Về trang chủ</Button></Link>
                </div>
            </div>
        );
    }

    return (
    (!loadState.class && loaded) ? <Redirect from="*" to="/" /> :
        <>
        
            {(loadState.class) &&
                <>
                    {ROLE_GROUPS.STAFF.includes(userData.role) &&
                        <>
                            <Switch>
                            <PrivateRoute exact path="/:classID/" component={Home} />
                            <PrivateRoute exact path="/:classID/dashboard" component={Dashboard} />
                            <PrivateRoute exact path="/:classID/studentinfo" component={StudentInfoList} />
                            <PrivateRoute exact path="/:classID/studentscore" component={StudentScoreList} />
                            <PrivateRoute exact path="/:classID/attendance" component={AttendancePage} />
                            <PrivateRoute exact path="/:classID/home-assignments" component={HomeAssignmentPage} />
                            <PrivateRoute exact path="/:classID/tuition" component={TuitionPage} />
                            <Redirect from="*" to={`/${classSlug(classWrapper.curClass)}/dashboard`} />
                            </Switch>
                        </>
                    }
                    {(userData.role === ROLES.STUDENT) &&
                        <>
                            <Switch>
                            <PrivateRoute exact path="/" component={Home} />
                            {/* <PrivateRoute exact path="/:classID/studentinfo" component={StudentInfoList} />
                            <PrivateRoute exact path="/:classID/studentscore" component={StudentScoreList} /> */}
                            <PrivateRoute exact path="/stuhome" component={StuHome} />
                            <PrivateRoute exact path="/:classID/attendance" component={AttendancePage} />
                            <PrivateRoute exact path="/:classID/home-assignments" component={HomeAssignmentPage} />
                            <PrivateRoute path="/personalscore" component={PersonalScore}/>
                            <Redirect from="*" to={`/stuhome`} />
                            </Switch>
                        </>
                    }
                    {(userData.role === ROLES.ADMIN) &&
                        <>
                            <Switch>
                            {/* route admin here */}
                            </Switch>
                        </>
                    }
                </>
            }
            
        </>
    );
  }

function ClassNameDisplay(){
    const auth = useRecoilValue(authAtom);
    const classWrapper = useClassWrapper();
    if (auth) {
        if (classWrapper.curClass) {
            const name = classWrapper.curClass.class_name || null;
            if (classWrapper.curClass.archived) {
                return <span>{name} <span style={{ color: 'var(--warn)', fontWeight: 'normal' }}>(đã lưu trữ — chỉ đọc)</span></span>;
            }
            // The picker separates trial classes into their own mode, so once you're inside one this
            // header is the only thing still saying so — and a trial class behaves differently
            // (no attendance marking, no quota).
            if (classWrapper.curClass.is_trial) {
                return <span>{name} <span style={{ color: 'var(--info)', fontWeight: 'normal' }}>(học thử)</span></span>;
            }
            return name;
        }
        return "Chưa chọn lớp";
    }
    localStorage.removeItem('currentClass');
    return null;
}
